import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AOS from 'aos';
import { supportAPI } from '../services/api';

const IN_APP_CATEGORY_OPTIONS = [
  { value: '', label: 'Category' },
  { value: 'General Inquiry', label: 'General Inquiry' },
  { value: 'Pricing & Plans', label: 'Pricing & Plans' },
  { value: 'Payments & Subscription', label: 'Payments & Subscription' },
  { value: 'Feedback or Feature Request', label: 'Feedback or Feature Request' },
  { value: 'Bug or Technical Issue', label: 'Bug or Technical Issue' },
  { value: 'Other', label: 'Other' },
];

const INITIAL_FORM_DATA = { category: '', message: '', company_name: '' };
const INITIAL_ERRORS = { category: '', message: '' };

function HelpCenter() {
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState(INITIAL_ERRORS);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    AOS.refresh();
  }, []);

  useEffect(() => {
    if (submitted) {
      const t = requestAnimationFrame(() => AOS.refresh());
      return () => cancelAnimationFrame(t);
    }
  }, [submitted]);

  const setField = (field) => (value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const next = { ...INITIAL_ERRORS };
    let valid = true;
    if (!formData.category?.trim()) {
      next.category = 'Please select a category.';
      valid = false;
    }
    if (!formData.message?.trim()) {
      next.message = 'Message is required.';
      valid = false;
    }
    setErrors(next);
    return valid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      setIsSubmitting(true);
      setSubmitError('');
      await supportAPI.submitHelp({
        category: formData.category.trim(),
        message: formData.message.trim(),
        company_name: formData.company_name.trim(),
      });
      setFormData(INITIAL_FORM_DATA);
      setErrors(INITIAL_ERRORS);
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F5F4FF]">
        <main className="flex justify-center px-4 sm:px-6 pb-[148px]">
          <section className="w-full max-w-[550px] mx-auto pt-[160px]" data-aos="fade-up" data-aos-duration="500">
            <h1 className="font-inter font-normal text-[40px] text-center text-[#0F172A] mb-[24px]">
              Message sent! 🎉
            </h1>
            <p className="font-inter font-normal text-[16px] text-center mb-6">
              Thanks for reaching out. Our team has received your message and will get back to you within 24-48 hours.
            </p>
            <p className="font-inter font-normal text-[16px] text-center text-[#0F172A] mb-10">
              You can continue reviewing while we take a look.
            </p>
            <div className="flex justify-center mt-10">
              <Link
                to="/dashboard/all-reviewers"
                className="rounded-lg py-3 px-7 bg-[#FFC92A] text-[#421A83] font-roboto font-medium text-[16px] leading-6 tracking-[0.5px] inline-block transition-opacity hover:opacity-95"
              >
                Back to Reviewers
              </Link>
            </div>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F4FF]">
      <main className="max-w-[720px] mx-auto px-[24px] py-[40px]">
        <h1
          className="font-inter font-normal text-[40px] text-[#0F172A] mb-2.5 text-center"
          data-aos="fade-up"
          data-aos-duration="500"
        >
          Help Center
        </h1>
        <p
          className="font-inter font-normal text-[16px] text-[#45464E] text-center mb-10"
          data-aos="fade-up"
          data-aos-duration="500"
          data-aos-delay="50"
        >
          Need help, have feedback, or something not working right? We've got you. 💜
        </p>

        {/* Still need help - form from Contact.js */}
        <section>
          <form
            onSubmit={handleSubmit}
            noValidate
            className="bg-white rounded-xl p-6 sm:p-10 shadow-sm border border-[#EFF0F6] relative"
            data-aos="fade-up"
            data-aos-duration="500"
            data-aos-delay="100"
          >
            <div className="mb-4">
              <label htmlFor="help-category" className="font-inter font-medium text-sm text-[#111927]">
                Category
              </label>
              <select
                id="help-category"
                value={formData.category}
                onChange={(e) => setField('category')(e.target.value)}
                required
                aria-required
                aria-invalid={Boolean(errors.category)}
                className={`w-full mt-1 h-12 rounded-lg border bg-white pl-3 pr-10 py-3 outline-none transition-colors focus:border-[#6E43B9] appearance-none cursor-pointer font-inter font-medium text-sm text-[#111927] ${!formData.category ? 'text-[#6C737F]' : ''} ${errors.category ? 'border-red-500' : 'border-[#D2D6DB]'}`}
              >
                {IN_APP_CATEGORY_OPTIONS.map((opt) => (
                  <option key={opt.value || 'empty'} value={opt.value} disabled={!opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {errors.category && (
                <p className="mt-1 font-inter text-xs text-red-500" role="alert">
                  {errors.category}
                </p>
              )}
            </div>

            <div className="mt-4">
              {submitError && (
                <p className="mb-3 font-inter text-sm text-red-500" role="alert">
                  {submitError}
                </p>
              )}
              <textarea
                id="message"
                value={formData.message}
                onChange={(e) => setField('message')(e.target.value)}
                rows={5}
                required
                aria-required
                aria-invalid={Boolean(errors.message)}
                aria-describedby={errors.message ? 'message-error' : undefined}
                className={`w-full mt-1 rounded-lg border bg-white p-3 outline-none transition-colors focus:border-[#6E43B9] resize-y placeholder:text-[#6C737F] font-inter font-medium text-sm tracking-[0.15px] text-[#111927] ${errors.message ? 'border-red-500' : 'border-[#D2D6DB]'}`}
                placeholder="Tell us what happened or what you need help with. The more details, the better 😉"
              />
              {errors.message && (
                <p id="message-error" className="mt-1 font-inter text-xs text-red-500" role="alert">
                  {errors.message}
                </p>
              )}
            </div>

            {/* Honeypot – hidden from users */}
            <div className="absolute -left-[9999px] top-0 opacity-0" aria-hidden="true">
              <label htmlFor="help_company_name">Company name</label>
              <input
                type="text"
                id="help_company_name"
                name="company_name"
                tabIndex={-1}
                autoComplete="off"
                value={formData.company_name}
                onChange={(e) => setField('company_name')(e.target.value)}
              />
            </div>

            <div className="mt-10">
              <button
                type="submit"
                disabled={isSubmitting}
                className="rounded-lg py-3 px-7 bg-[#FFC92A] text-[#421A83] font-roboto font-medium text-base tracking-[0.5px] transition-opacity hover:opacity-95 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Sending...
                  </>
                ) : (
                  'Send Message'
                )}
              </button>
              <p className="font-inter font-normal text-xs tracking-[0.4px] text-[#6C737F] mt-4">
                We usually reply within 24-48 hours.
              </p>
            </div>
          </form>

          <p
            className="font-inter font-normal text-sm text-center text-[#45464E] mt-6"
            data-aos="fade-up"
            data-aos-duration="500"
          >
            We read every message — promise. 💜
          </p>
        </section>
      </main>
    </div>
  );
}

export default HelpCenter;
